// @ts-nocheck
import { randCatchPhrase, randParagraph } from '@ngneat/falso';
import { getDTBLock, updateDTB, removeDTBLock, takeDTBLock } from '../Library/_data_sources/manageDTB';
import { getDTBs } from '../Library/_data_sources/useDTBs';

import { getIssuedManual } from '../../Policy/IssuedManuals/_data_sources/useIssuedManual';
import {
  baseApiUrl,
  fetcher,
  ServerErrorResponse,
  createParamString,
} from 'data_sources/constants';
import contentHelper from 'helpers/contentHelper';

interface UpdateDTBsConfig {
  dtbIds?: string[];
  updateContent?: boolean;
  updateReferences?: boolean;
  addRandomContent?: boolean;
  status?: string;
  limit?: number;
}

// Function to fetch issued manuals using the same logic as useIssuedManuals
const getIssuedManuals = async (organizationId: string, params = {}) => {
  const url = `${baseApiUrl()}/lexipub/manuals/issued/v1/manuals`;
  
  try {
    const paramString = createParamString(params);
    const data = await fetcher({
      method: 'get',
      url: `${url}?${paramString}`,
    });

    return convertResults(data);
  } catch (error: unknown) {
    console.error('Error fetching issued manuals:', error);
    throw error;
  }
};

// Convert results using the same logic as useIssuedManuals
const convertResults = (data: any) => ({
  count: data.count,
  offset: data.offset || 0,
  content: data.content.map((x: any) => ({
    authorizations: x.authorizations,
    id: x.id,
    manualType: {
      key: x.manualType?.key,
      name: x.manualType?.value,
    },
    name: contentHelper.decodeHtmlEntities(x.name?.value || ''),
    organizationId: x.organizationId,
    s3Location: x.s3Location,
    createdAt: x.createdAt,
    jurisdiction: x.jurisdiction,
    vertical: {
      key: x.vertical?.key,
      name: x.vertical?.value,
    },
  })),
});

// Function to fetch a single DTB using the same logic as useDTB
const getDTB = async (orgId: string, dtbId: string) => {
  if (!orgId || !dtbId) {
    throw new Error('Organization ID and DTB ID are required');
  }

  const url = `${baseApiUrl()}/lexiverse/dtbs/v1/organizations/${orgId}/dtbs/${dtbId}`;

  try {
    const response = await fetcher({
      method: 'GET',
      url,
    });
    return response;
  } catch (error: unknown) {
    console.error('Error fetching DTB:', error);
    throw error;
  }
};

const SAMPLE_CONTENT_BLOCKS = [
  {
    content: 'This is a sample scenario that demonstrates proper procedure.',
    contentEditable: 'This is a sample scenario that demonstrates proper procedure.',
    contentType: 'CONTENT',
    title: 'Scenario',
  },
  {
    content: 'The applicable rule states that officers must follow established protocols.',
    contentEditable: 'The applicable rule states that officers must follow established protocols.',
    contentType: 'CONTENT',
    title: 'Rule',
  },
  {
    content: 'Analysis of the situation shows that proper training is essential.',
    contentEditable: 'Analysis of the situation shows that proper training is essential.',
    contentType: 'CONTENT',
    title: 'Analysis',
  },
  {
    content: 'The conclusion emphasizes the importance of following department guidelines.',
    contentEditable: 'The conclusion emphasizes the importance of following department guidelines.',
    contentType: 'CONTENT',
    title: 'Conclusion',
  },
];

declare global {
  interface Window {
    updateDTBs: (config?: UpdateDTBsConfig) => Promise<void>;
  }
}

window.updateDTBs = async (config: UpdateDTBsConfig = {}) => {
  console.log('Starting updateDTBs...', config);
  
  try {
    const {
      dtbIds = [],
      updateContent = true,
      updateReferences = true,
      addRandomContent = false,
      status = 'ALL',
      limit = 100
    } = config;

    // Get current organization ID from localStorage
    const orgId = localStorage.getItem('phx-currentOrganizationId');
    if (!orgId) {
      throw new Error('Could not find organizationId in localStorage');
    }
    console.log('Organization ID:', orgId);

    // Get current user information
    const currentProfile = JSON.parse(localStorage.getItem('phx-currentProfile') || '{}');
    const currentUserId = currentProfile?.user?.id;
    if (!currentUserId) {
      throw new Error('Could not find current user information');
    }
    console.log('Current User ID:', currentUserId);

    // Fetch issued manuals to get jurisdiction information
    console.log('Fetching issued manuals...');
    const issuedManualsResponse = await getIssuedManuals(orgId, { limit: 100 });
    const issuedManuals = issuedManualsResponse.content;
    
    // Find the first manual with jurisdiction set
    const manualWithJurisdiction = issuedManuals.find(manual => manual.jurisdiction);
    
    if (!manualWithJurisdiction) {
      console.warn('No issued manuals found with jurisdiction set');
    } else {
      console.log('Using manual with jurisdiction:', {
        manualId: manualWithJurisdiction.id,
        manualName: manualWithJurisdiction.name,
        jurisdiction: manualWithJurisdiction.jurisdiction
      });
    }

    // If updateReferences is true, fetch all policies from the manual
    let allPolicies = [];
    if (updateReferences && manualWithJurisdiction) {
      console.log('Fetching manual data for policy references...');
      try {
        const manual = await getIssuedManual(manualWithJurisdiction.id);
        console.log('Manual data fetched for references:', manual);

        if (manual && manual.chapters) {
          // Collect all policies from all chapters
          allPolicies = manual.chapters.flatMap(chapter => 
            chapter.policies?.map(policy => ({
              id: policy.id,
              title: policy.title,
              chapterTitle: chapter.title
            })) || []
          );

          if (allPolicies.length > 0) {
            console.log(`Found ${allPolicies.length} policies in manual for references`);
          } else {
            console.warn('No policies found in manual for references');
          }
        }
      } catch (error) {
        console.error('Error fetching manual data for references:', error);
      }
    }

    // Get all DTBs using getDTBs
    console.log('Fetching all DTBs...');
    const dtbsResponse = await getDTBs(orgId, { status, limit });
    const allDTBs = dtbsResponse.content;

    console.log(`Found ${allDTBs.length} DTBs total`);

    // Filter DTBs if specific IDs are provided
    let dtbsToUpdate = allDTBs;
    if (dtbIds.length > 0) {
      dtbsToUpdate = allDTBs.filter(dtb => dtbIds.includes(dtb.id));
      console.log(`Filtered to ${dtbsToUpdate.length} DTBs based on provided IDs`);
    }

    if (dtbsToUpdate.length === 0) {
      console.log('No DTBs found to update');
      return;
    }

    console.log(`Updating ${dtbsToUpdate.length} DTBs`);

    // Update each DTB
    const updatedDTBs = [];
    for (let i = 0; i < dtbsToUpdate.length; i++) {
      const dtb = dtbsToUpdate[i];
      console.log(`Updating DTB ${i + 1}/${dtbsToUpdate.length}: ${dtb.id} - ${dtb.name}`);

      try {
        // Get current DTB data (in case we need the full data)
        const currentDTB = await getDTB(orgId, dtb.id);

        console.log(`Current DTB data fetched: ${dtb.id}`);

        // Check if DTB is locked and handle lock acquisition
        if (currentDTB.lock) {
          if (currentDTB.lock.userId === currentUserId) {
            console.log(`DTB ${dtb.id} is already locked by current user`);
          } else {
            console.log(`DTB ${dtb.id} is locked by another user (${currentDTB.lock.userId}), taking lock...`);
            await new Promise((resolve, reject) => {
              takeDTBLock(orgId, dtb.id, resolve, reject);
            });
            console.log(`Lock taken from other user for DTB: ${dtb.id}`);
          }
        } else {
          console.log(`DTB ${dtb.id} is not locked, acquiring lock...`);
          await new Promise((resolve, reject) => {
            getDTBLock(orgId, dtb.id, resolve, reject);
          });
          console.log(`Lock acquired for DTB: ${dtb.id}`);
        }

        // Prepare update data
        const updateData = { ...currentDTB };

        // Update with manual and jurisdiction information if available
        if (manualWithJurisdiction) {
          updateData.level = 'AGENCY';
          updateData.manual = {
            id: manualWithJurisdiction.id,
            name: manualWithJurisdiction.name,
          };
          updateData.jurisdiction = manualWithJurisdiction.jurisdiction;
          updateData.contentBlocks = SAMPLE_CONTENT_BLOCKS;
          console.log(`Updated DTB ${dtb.id} with manual: ${manualWithJurisdiction.name} and jurisdiction: ${manualWithJurisdiction.jurisdiction.name}`);
        }

        if (updateContent && addRandomContent) {
          // Update content blocks with random content
          updateData.contentBlocks = SAMPLE_CONTENT_BLOCKS.map(block => ({
            ...block,
            content: randParagraph(),
            contentEditable: randParagraph(),
          }));
        }

        if (updateReferences) {
          // Add or update content references using random policy from manual
          if (allPolicies.length > 0) {
            const selectedPolicy = allPolicies[Math.floor(Math.random() * allPolicies.length)];
            updateData.contentReferences = [{
              contentUri: `xref://manuals/${manualWithJurisdiction.id}/content/${selectedPolicy.id}`,
              name: `${selectedPolicy.chapterTitle}: ${selectedPolicy.title}`
            }];
            console.log(`Added policy reference to DTB ${dtb.id}: ${selectedPolicy.chapterTitle}: ${selectedPolicy.title}`);
          } else {
            // Fallback to sample reference if no policies found
            updateData.contentReferences = [
              {
                contentUri: `xref://manuals/sample-manual/content/sample-policy`,
                name: `Sample Policy Reference`
              }
            ];
            console.log(`Added sample policy reference to DTB ${dtb.id} (no policies found in manual)`);
          }
        }

        // Update DTB
        await new Promise((resolve, reject) => {
          updateDTB(orgId, dtb.id, updateData, resolve, reject);
        });

        console.log(`DTB updated: ${dtb.id}`);

        // Remove lock
        await new Promise((resolve, reject) => {
          removeDTBLock(orgId, dtb.id, resolve, reject);
        });

        console.log(`Lock removed from DTB: ${dtb.id}`);

        updatedDTBs.push(dtb.id);
      } catch (error) {
        console.error(`Error updating DTB ${dtb.id}:`, error);
      }
    }

    console.log(`Successfully updated ${updatedDTBs.length} DTBs`);
    console.log('Updated DTB IDs:', updatedDTBs);
  } catch (error) {
    console.error('Error in updateDTBs:', error);
  }
}; 